import { listTransactionByCorrelationUUID } from "@api/Tracking"
import CSSelectInput, { CSSelectValue } from "@components/Form/CSSelectInput"
import CSTable, { Column } from "@components/Table/CSTable"
import Localization from "@localization/Index"
import { formatDurationFromMilliseconds } from "@utils/DateUtils"
import { closeWaiting, showError, showWaiting } from "@utils/NotificationsUtils"
import { copyToClipboard, HighlightedText } from "@utils/StringUtils"
import { getTransactionTypeTheme } from "@utils/TrackingUtils"
import { EntityListResponseTransaction, Transaction } from "cs.node.utils/csapi/provisioning"
import { format, parseISO } from "date-fns"
import { useState } from "react"
import { Badge, Modal } from "react-bootstrap"
import { ToastContainer } from "react-toastify"
import TransactionDetails from "./TransactionDetails"

type Props = {
    correlation_uuid: string
    trackingStyle?: "button" | "icon" | "link"
}

export type TransactionSelected = Transaction & { index: number }

const filterPayloadTypeOptions = [{label: "Request", value: "REQUEST"}, {label: "Response", value: "RESPONSE"}]

export const Tracking = (props: Props) =>{
    const { correlation_uuid, trackingStyle="icon" } = props
    const [trackingOpen, setTrackingOpen] = useState(false)
    const [transactionListResponse, setTransactionListResponse] = useState<EntityListResponseTransaction>()
    const [transactionSelected, setTransactionSelected] = useState<TransactionSelected>()
    const [filterPayloadType, setFilterPayloadType] = useState<"REQUEST"| "RESPONSE">("REQUEST")
    const [searchTerm, setSearchTerm] = useState<string>("")

    const loadTracking = async ()=>{
        setTransactionSelected(undefined)
        if(transactionListResponse) {
            return setTrackingOpen(true)
        }
        try{
            showWaiting(Localization.ATTENDERE)
            const response = await listTransactionByCorrelationUUID(correlation_uuid, 0, 2500)
            setTransactionListResponse(response)
            closeWaiting()
            return setTrackingOpen(true)

        } catch(err) {
            closeWaiting()
            setTransactionListResponse(undefined)
            return showError(Localization.ERRORE_GENERICO, err)
        }
    }

    const changeTransactionSelected = (index: number) =>{
        const newTransactionSelected = transactions[index]
        if(!newTransactionSelected) return;
        return setTransactionSelected({...newTransactionSelected, index})
    }

    const total_items = transactionListResponse?._metadata?.total_items ?? 0
    const value: CSSelectValue = {
        formControlValue: searchTerm.trim(),
        selectValue: filterPayloadType
    }

    const transactions = transactionListResponse?.content ?? []

    const filteredTransactions = (searchTerm.trim().length===0 || !filterPayloadType)?transactions:transactions.filter(transaction=>{
        if(filterPayloadType === "REQUEST") {
            return transaction.req?.p?.toLowerCase()?.includes(searchTerm.trim().toLowerCase())
        } else {
            return transaction.res?.p?.toLowerCase()?.includes(searchTerm.trim()?.toLowerCase())
        }
    })

    const columns: Column[] = [
        {
            name: "#",
            field: "_id",
            width: 5,
            render: (row: Transaction) =>{
                const index = transactions.findIndex(transaction=>transaction._id === row._id)
                return <span className="fw-bold">{index+1}</span>
            }
        },
        {
            name: Localization.TRACKING.TABLE.ID,
            field: "_id",
            width: 30,
            render: (row: Transaction) =>{
                return <>
                    <Badge bg="info">{row._id}</Badge>
                    {searchTerm.trim().length > 0 && filterPayloadType && <div className="small mt-1 d-flex align-items-center justify-content-start">
                        <Badge className="me-2" bg={filterPayloadType==="REQUEST"?"success":"danger"}>{filterPayloadType==="REQUEST"?"REQ":"RES"}</Badge>
                        {<HighlightedText
                            fullText={(filterPayloadType==="REQUEST"?row.req?.p:row.res?.p) ?? ""}  
                            searchTerm={searchTerm.trim()}
                            contextBefore={15}
                            contextAfter={15}
                        />}
                    </div>}
                </>
            }
        },
        {
            name: Localization.TRACKING.TABLE.TIPO,
            field: "t",
            width: 10,
            render: (row: Transaction) =>{
                const theme = getTransactionTypeTheme(row.t)

                return <>
                    <Badge bg={theme.variant}>{row.t}</Badge>
                </>
            }
        },
        {
            name: Localization.TRACKING.TABLE.TEMPO_DI_RISPOSTA,
            field: "msecs",
            align: 'start',
            width: 40,
            render: (row: Transaction) =>{
                const { req, res, msecs } = row
                if(!msecs || msecs === 0) return <></>
                const startDate = (req?.date?.$date)?format(parseISO(req.date.$date), 'dd/MM/yyyy HH:mm:ss:SSS'):"N.D."
                const endDate = (res?.date?.$date)?format(parseISO(res.date.$date), 'dd/MM/yyyy HH:mm:ss:SSS'):"N.D."
                const formatMsec = formatDurationFromMilliseconds(msecs)
                const msecVariant = (msecs < 5000)?"dark":(msecs< 120000)?"warning":"danger"

                return <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex flex-column align-items-end justify-content-start me-2">{startDate}</div>
                    <i className="bi-arrow-right text-success" />
                    <Badge bg={msecVariant}>{formatMsec}</Badge>
                    <i className="bi-arrow-right text-danger" />
                    <div className="d-flex flex-column align-items-start justify-content-start ms-2">{endDate}</div>
                </div>
            }
        }
    ]

    const contextMenu = [
        {
            text: Localization.TRACKING.TABLE.CONTEXT.MOSTRA_DETTAGLIO,
            action: (row: Transaction)=>{
                const index = transactions.findIndex(transaction=>transaction._id === row._id)
                return setTransactionSelected({...row, index})
            },
            default: true
        },
        {
            text: Localization.TRACKING.TABLE.CONTEXT.COPIA_ID,
            action: (row: Transaction)=>{
                if(!row._id) return false

                return copyToClipboard({text: row._id})
            },
            default: true
        }
    ]

    return <>
        {trackingStyle === "icon" && <i role="button" className="ms-2 bi-binoculars font-1-5x text-info" onClick={()=>loadTracking()} />}
        {trackingStyle === "button" && <button className="btn bt-info" onClick={()=>loadTracking()}>{Localization.TRACKING.LINK}</button>}
        {trackingStyle === "link" && <span className="cs-link" onClick={()=>loadTracking()}>{Localization.TRACKING.LINK}</span>}
        {trackingOpen && transactionListResponse && <Modal size="xl" show={trackingOpen} onHide={()=>setTrackingOpen(false)}>
            <Modal.Header closeButton>
                <Modal.Title><h1>{Localization.TRACKING.MODAL_TITLE(correlation_uuid)}</h1></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!transactionSelected && <>
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="fw-bold fs-3">{Localization.RISULTATI(total_items)}</div>
                        <div className="d-flex align-items-center justify-content-end">
                            <CSSelectInput 
                                controlId="transaction_search" 
                                placeholder=""
                                onChange={(values: CSSelectValue)=>{
                                    setSearchTerm(values.formControlValue ?? "")
                                    setFilterPayloadType((values.selectValue as "REQUEST" | "RESPONSE") ?? "REQUEST")
                                }}
                                selectOptions={filterPayloadTypeOptions}
                                value={value}
                                inputTextWidth={"300px"}
                            />
                        </div>
                    </div>
                    <CSTable client
                        hover
                        columns={columns}
                        data={filteredTransactions}
                        pagination={"END"}
                        pageSizeSelect={[20,50,100]}
                        totalEntries={total_items}
                        enableGotoPage={"CHANGE"}
                        loading={false}
                        contextMenu={contextMenu}
                    />
                </>}
                {transactionSelected && <TransactionDetails 
                    totalTransactions={transactions.length}
                    transaction={transactionSelected}
                    onChangeIndex={(index:number)=>changeTransactionSelected(index)}
                    onClose={()=>setTransactionSelected(undefined)} 
                />}
            </Modal.Body>
        </Modal>}
        <ToastContainer />
    </>
}

export default Tracking